'use client';
import React, { useEffect, useCallback } from 'react';
import { useCheckoutStore } from '../store/checkoutStore';
import PaymentInstructionsStep from './checkout/PaymentInstructionsStep';
import AwaitingHashStep from './checkout/AwaitingHashStep';
import VerifyingStep from './checkout/VerifyingStep';
import SuccessStep from './checkout/SuccessStep';
import ErrorStep from './checkout/ErrorStep';

export default function CheckoutModal() {
  const { isOpen, step, closeModal } = useCheckoutStore();

  const handleBackdropClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && step !== 'verifying' && step !== 'success') {
      closeModal();
    }
  }, [step, closeModal]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && step !== 'verifying' && step !== 'success') {
        closeModal();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [isOpen, step, closeModal]);

  if (!isOpen) return null;

  return (
    <div
      className="modal-backdrop fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 animate-fade-in"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-label="Checkout"
    >
      {/* On mobile: full-width bottom sheet. On sm+: centered card */}
      <div
        className="w-full sm:max-w-lg animate-scale-in flex flex-col"
        style={{ maxHeight: '100dvh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal container */}
        <div className="card-elevated sm:rounded-2xl rounded-t-2xl overflow-hidden glow-gold flex flex-col" style={{ maxHeight: '100dvh' }}>
          {/* Modal header — always visible */}
          <div className="flex-shrink-0 flex items-center justify-between px-4 sm:px-6 py-4 sm:py-5 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{ background: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.2)' }}>
                <span className="text-primary font-black text-sm">₿</span>
              </div>
              <div>
                <h2 className="text-base font-bold text-foreground">Luminary X Pro</h2>
                <p className="text-xs text-muted-foreground">Bitcoin Checkout</p>
              </div>
            </div>

            {/* Step indicator + close */}
            <div className="flex items-center gap-3">
              <StepBreadcrumb step={step} />
              {step !== 'verifying' && step !== 'success' && (
                <button
                  onClick={closeModal}
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-150"
                  aria-label="Close checkout"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Modal body — scrollable */}
          <div className="flex-1 overflow-y-auto overscroll-contain p-4 sm:p-6">
            {step === 'payment_instructions' && <PaymentInstructionsStep />}
            {step === 'awaiting_hash' && <AwaitingHashStep />}
            {step === 'verifying' && <VerifyingStep />}
            {step === 'success' && <SuccessStep />}
            {step === 'error' && <ErrorStep />}
          </div>
        </div>
      </div>
    </div>
  );
}

function StepBreadcrumb({ step }: { step: string }) {
  const steps = [
    { id: 'payment_instructions', label: '1' },
    { id: 'awaiting_hash', label: '2' },
    { id: 'verifying', label: '3' },
    { id: 'success', label: '✓' },
  ];

  const activeIndex = steps.findIndex((s) => s.id === step);

  return (
    <div className="flex items-center gap-1.5">
      {steps.map((s, i) => {
        const isComplete = i < activeIndex;
        const isActive = s.id === step;
        return (
          <React.Fragment key={`breadcrumb-${s.id}`}>
            <div className={`step-indicator text-xs ${
              isComplete ? 'step-complete' : isActive ? 'step-active' : 'step-pending'
            }`}>
              {isComplete ? (
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              ) : s.label}
            </div>
            {i < steps.length - 1 && (
              <div className={`w-4 h-px transition-colors duration-300 ${i < activeIndex ? 'bg-green-500' : 'bg-border'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}